/**
 * operationalBundleAttestationHash
 *
 * Deterministic hash computation for attestation signing.
 *
 * Signs a canonical, stable representation of the bundle's core integrity
 * fields — NOT the raw JSON. This ensures the signature captures the
 * semantically relevant content without being brittle to JSON formatting.
 */

import { computeSha256Hex } from '../../server/export/operationalBundleHash.js';
import type { AttestationHashInput } from './operationalBundleAttestationTypes.js';

/**
 * Build the canonical attestation payload from bundle integrity fields.
 * The payload is a deterministic JSON string with sorted keys.
 */
function buildAttestationPayload(input: AttestationHashInput): string {
  const payload = {
    bundleHash: input.bundleHash,
    operationalWorkspaceHash: input.operationalWorkspaceHash,
    eventStreamHash: input.eventStreamHash,
    paneModelHashes: [...input.paneModelHashes].sort((a, b) => a.paneId.localeCompare(b.paneId)),
    continuityState: input.continuityState,
    proofOnly: input.proofOnly,
    attestationScope: input.attestationScope,
    attestedAt: input.attestedAt,
  };

  return JSON.stringify(payload, sortedReplacer);
}

/**
 * Compute the SHA-256 hex hash of the canonical attestation payload.
 */
export async function computeAttestationHash(input: AttestationHashInput): Promise<string> {
  const payload = buildAttestationPayload(input);
  return computeSha256Hex(payload);
}

/**
 * Compute the hash that will be signed. This is a stable, deterministic
 * representation of the bundle's integrity-relevant fields.
 */
export async function computeSignedBundleHash(
  bundleHash: string,
  attestationScope: string,
  attestedAt: string
): Promise<string> {
  const payload = JSON.stringify({
    bundleHash,
    attestationScope,
    attestedAt,
  }, sortedReplacer);

  return computeSha256Hex(payload);
}

function sortedReplacer(_key: string, value: unknown): unknown {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const sorted: Record<string, unknown> = {};
  for (const k of Object.keys(value as Record<string, unknown>).sort()) {
    sorted[k] = (value as Record<string, unknown>)[k];
  }
  return sorted;
}
